import type { NewsItem } from "@/mocks/blackdata";

interface Props {
  id: string;
  eyebrow: string;
  title: string;
  lead: string;
  items: NewsItem[];
  variant: "cards" | "rows";
  sideNote: string;
  sideTag: string;
  accent: "primary" | "secondary";
}

export default function SectionList({ id, eyebrow, title, lead, items, variant, sideNote, sideTag, accent }: Props) {
  const iconDot = accent === "primary" ? "bg-primary-600" : "bg-secondary-600";
  const titleColor = accent === "primary" ? "text-primary-700" : "text-secondary-700";

  return (
    <section id={id} className="max-w-[1400px] mx-auto px-3 md:px-6 pt-8 md:pt-12 scroll-mt-20">
      <div className="flex flex-col gap-1 pb-4 md:pb-5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className={`inline-flex items-center gap-1 rounded ${iconDot} text-background-50 px-2 py-0.5 text-[11px] whitespace-nowrap`}>
            <i className="ri-stack-fill w-3.5 h-3.5 flex items-center justify-center"></i>
            {eyebrow}
          </span>
          <span className="text-[12px] text-foreground-400">{sideNote}</span>
          <span className="text-[12px] text-foreground-400 hidden sm:inline">{sideTag}</span>
        </div>
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className={`font-heading text-xl md:text-3xl ${titleColor}`}>{title}</h2>
            <p className="text-[13px] md:text-sm text-foreground-600 mt-1">{lead}</p>
          </div>
          <a href="#rank" className="hidden md:inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-foreground-300 text-foreground-700 hover:bg-background-100 px-3.5 py-2 text-sm transition-colors">
            查看全部分区<i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
          </a>
        </div>
      </div>

      {variant === "rows" ? (
        <div className="flex flex-col gap-3 md:gap-4">
          {items.map((it) => (
            <article key={it.id} className="group grid grid-cols-1 sm:grid-cols-12 gap-3 md:gap-5 items-stretch bg-background-100 border border-background-200 rounded-lg overflow-hidden hover:border-background-300 transition-colors">
              <div className="sm:col-span-5 relative overflow-hidden bg-foreground-900 min-h-[150px] sm:min-h-[190px]">
                <img src={it.image} alt={it.title} className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded bg-background-950/80 text-background-100 px-2 py-1 text-[11px] backdrop-blur">
                  <i className="ri-scales-3-line w-3.5 h-3.5 flex items-center justify-center text-accent-500"></i>
                  {it.tag}
                </span>
              </div>
              <div className="sm:col-span-7 flex flex-col justify-center gap-2 p-4 md:p-6">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`text-[11px] font-medium ${titleColor}`}>{eyebrow}</span>
                  <span className="text-[12px] text-foreground-400">{it.time}</span>
                  <span className="text-[12px] text-accent-700 inline-flex items-center gap-1 ml-auto">
                    <i className="ri-fire-line w-3.5 h-3.5 flex items-center justify-center"></i>{it.heat}
                  </span>
                </div>
                <h3 className="text-[15px] md:text-lg font-semibold text-foreground-950 leading-snug group-hover:text-primary-700 transition-colors">{it.title}</h3>
                <p className="text-[13px] md:text-sm text-foreground-600 leading-relaxed">{it.summary}</p>
                <div className="flex flex-wrap gap-x-5 gap-y-1 pt-1 text-[12px] text-foreground-500">
                  <span className="inline-flex items-center gap-1"><i className="ri-download-2-line w-3.5 h-3.5 flex items-center justify-center"></i>证据包可下载</span>
                  <span className="inline-flex items-center gap-1"><i className="ri-chat-check-line w-3.5 h-3.5 flex items-center justify-center"></i>逐条可核对</span>
                </div>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
          {items.map((it) => (
            <article key={it.id} className="group flex flex-col bg-background-100 border border-background-200 rounded-lg overflow-hidden hover:border-background-300 transition-colors">
              <div className="relative overflow-hidden bg-foreground-900 aspect-[16/9] min-h-[170px]">
                <img src={it.image} alt={it.title} className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-background-950/70 to-transparent"></div>
                <div className="absolute left-3 top-3 flex items-center gap-1.5">
                  <span className="rounded bg-background-950/80 text-background-100 px-2 py-1 text-[11px] backdrop-blur">{it.tag}</span>
                </div>
                <div className="absolute right-3 top-3 flex items-center gap-1 rounded bg-accent-500 text-background-50 px-2 py-1 text-[11px]">
                  <i className="ri-fire-fill w-3.5 h-3.5 flex items-center justify-center"></i>{it.heat}
                </div>
              </div>
              <div className="flex flex-col flex-1 gap-2 p-4">
                <h3 className="text-[15px] md:text-base font-semibold text-foreground-950 leading-snug group-hover:text-secondary-700 transition-colors line-clamp-2">{it.title}</h3>
                <p className="text-[13px] text-foreground-600 leading-relaxed line-clamp-2">{it.summary}</p>
                <div className="mt-auto flex items-center justify-between pt-2">
                  <span className="text-[12px] text-foreground-400">{it.time}</span>
                  <a href="#drama" className="inline-flex items-center gap-1 text-[13px] text-secondary-700 font-medium whitespace-nowrap">
                    查看证据与切片<i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}